import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useDropdownMenu } from "./context";

// Animation styles injected once
const ANIMATION_STYLES = `
@keyframes dropdownMenuFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes dropdownMenuFadeOut {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.95);
  }
}
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.textContent = ANIMATION_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
}

type Align = "start" | "center" | "end";
type Side = "top" | "right" | "bottom" | "left";

interface DropdownMenuContentProps {
  children: React.ReactNode;
  /** The preferred side of the trigger to render against */
  side?: Side;
  /** The horizontal alignment relative to the trigger */
  align?: Align;
  /** The distance in pixels from the trigger */
  sideOffset?: number;
  /** Additional CSS classes */
  className?: string;
  /** Whether to render in a portal */
  portal?: boolean;
  /** Whether to flip to the opposite side when there is not enough space */
  avoidCollisions?: boolean;
  /** Render the content at the trigger's width (for select-style fields) */
  matchTriggerWidth?: boolean;
}

function computePosition(
  rect: DOMRect,
  side: Side,
  align: Align,
  sideOffset: number,
  matchTriggerWidth = false,
): React.CSSProperties {
  const style: React.CSSProperties = { position: "fixed", zIndex: 100 };

  if (matchTriggerWidth) style.width = rect.width;

  switch (side) {
    case "top":
      style.bottom = window.innerHeight - rect.top + sideOffset;
      break;
    case "bottom":
      style.top = rect.bottom + sideOffset;
      break;
    case "left":
      style.right = window.innerWidth - rect.left + sideOffset;
      break;
    case "right":
      style.left = rect.right + sideOffset;
      break;
  }

  if (side === "top" || side === "bottom") {
    switch (align) {
      case "start":
        style.left = rect.left;
        break;
      case "center":
        style.left = rect.left + rect.width / 2;
        style.transform = "translateX(-50%)";
        break;
      case "end":
        style.right = window.innerWidth - rect.right;
        break;
    }
  } else {
    switch (align) {
      case "start":
        style.top = rect.top;
        break;
      case "center":
        style.top = rect.top + rect.height / 2;
        style.transform = "translateY(-50%)";
        break;
      case "end":
        style.bottom = window.innerHeight - rect.bottom;
        break;
    }
  }

  return style;
}

/**
 * DropdownMenuContent - The popover content that appears when the dropdown is open
 */
export function DropdownMenuContent({
  children,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  className = "",
  portal = true,
  avoidCollisions = true,
  matchTriggerWidth = false,
}: DropdownMenuContentProps) {
  const { open, close, triggerRef } = useDropdownMenu();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [resolvedSide, setResolvedSide] = useState<Side>(side);
  const lockedPositionRef = useRef<React.CSSProperties | null>(null);

  // Inject animation styles on first render
  useEffect(() => {
    injectStyles();
  }, []);

  // Reset resolved side only when the preferred `side` prop changes. Do NOT
  // reset on `open` toggle: resetting during close fires mid-exit-animation,
  // which would flip `transformOrigin` while the fadeOut is scaling — visible
  // as a position/scale jump (the dropdown appears to reopen). The collision
  // logic below re-evaluates resolvedSide on the next open via the
  // `shouldRender` dep.
  useEffect(() => {
    setResolvedSide(side);
  }, [side]);

  // Flip side before paint if there is not enough space on the preferred side
  useLayoutEffect(() => {
    if (!shouldRender || !avoidCollisions) return;
    if (!contentRef.current || !triggerRef?.current) return;

    const trigger = triggerRef.current;
    const content = contentRef.current;
    const rect = trigger.getBoundingClientRect();
    const contentHeight = content.offsetHeight;
    const contentWidth = content.offsetWidth;

    if (side === "bottom") {
      const spaceBelow = window.innerHeight - rect.bottom - sideOffset;
      const spaceAbove = rect.top - sideOffset;
      if (contentHeight > spaceBelow && contentHeight <= spaceAbove) {
        setResolvedSide("top");
        return;
      }
    } else if (side === "top") {
      const spaceAbove = rect.top - sideOffset;
      const spaceBelow = window.innerHeight - rect.bottom - sideOffset;
      if (contentHeight > spaceAbove && contentHeight <= spaceBelow) {
        setResolvedSide("bottom");
        return;
      }
    } else if (side === "left") {
      const spaceLeft = rect.left - sideOffset;
      const spaceRight = window.innerWidth - rect.right - sideOffset;
      if (contentWidth > spaceLeft && contentWidth <= spaceRight) {
        setResolvedSide("right");
        return;
      }
    } else if (side === "right") {
      const spaceRight = window.innerWidth - rect.right - sideOffset;
      const spaceLeft = rect.left - sideOffset;
      if (contentWidth > spaceRight && contentWidth <= spaceLeft) {
        setResolvedSide("left");
        return;
      }
    }

    setResolvedSide(side);
  }, [shouldRender, side, sideOffset, avoidCollisions]);

  // Lock position when dropdown opens; recomputed only if resolvedSide changes.
  // Using a ref (not state) avoids the extra render that causes the open/close flicker.
  useLayoutEffect(() => {
    if (shouldRender && triggerRef?.current) {
      lockedPositionRef.current = computePosition(
        triggerRef.current.getBoundingClientRect(),
        resolvedSide,
        align,
        sideOffset,
        matchTriggerWidth,
      );
    }
    if (!shouldRender) {
      lockedPositionRef.current = null;
    }
  }, [shouldRender, resolvedSide, align, sideOffset, matchTriggerWidth]);

  // Close dropdown on scroll (standard dropdown UX behavior)
  useEffect(() => {
    if (!open) return;

    const handleScroll = (event: Event) => {
      // Don't close if the scroll happened inside the dropdown content itself
      if (contentRef.current?.contains(event.target as Node)) return;
      close();
    };

    // Listen for scroll on window and capture phase to catch all scroll events
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, close]);

  // Focus the first autoFocus input when the dropdown opens (e.g. search fields).
  // autoFocus on portal-rendered elements is unreliable — the trigger retains
  // focus after click, so we drive focus explicitly after mount.
  useEffect(() => {
    if (!open || !shouldRender) return;
    const input = contentRef.current?.querySelector<HTMLInputElement>("input[autofocus]");
    if (!input) return;
    const id = requestAnimationFrame(() => input.focus());
    return () => cancelAnimationFrame(id);
  }, [open, shouldRender]);

  // Handle open/close with animation. useLayoutEffect runs before paint so
  // the isAnimating state for the exit branch is committed before the browser
  // paints; otherwise the prior render's `animation: fadeIn` is applied to the
  // DOM, the browser snaps to that keyframe's `from` (opacity 0), then the
  // follow-up render switches to fadeOut and snaps to its `from` (opacity 1) —
  // producing a visible flicker on close.
  useLayoutEffect(() => {
    if (open) {
      setShouldRender(true);
      // rAF defers turning on the entry animation to the next frame so the
      // browser actually plays the keyframes instead of skipping straight to
      // the `to` state.
      requestAnimationFrame(() => setIsAnimating(true));
    } else if (shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [open, shouldRender]);

  // Handle click outside to close
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const content = contentRef.current;
      const trigger = triggerRef?.current;

      // Don't close if clicking inside content or trigger
      if (content?.contains(target) || trigger?.contains(target)) {
        return;
      }

      close();
    };

    // Use setTimeout to avoid immediate closing when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, close, triggerRef]);

  if (!shouldRender) return null;

  // Animation state machine:
  //   shouldRender && open && !isAnimating  -> pending fadeIn (invisible, waits for rAF)
  //   shouldRender && open && isAnimating   -> playing fadeIn
  //   shouldRender && !open                 -> playing fadeOut (regardless of isAnimating)
  //
  // The previous `isAnimating ? fadeIn : fadeOut` rule would briefly paint the
  // first close render (open=false, isAnimating=true) with `animation: fadeIn`
  // before useLayoutEffect's `setIsAnimating(false)` re-render landed. If the
  // browser painted that frame (due to render cascades from Apollo cache
  // updates after the mutation), the user saw fadeIn restart before fadeOut
  // played — perceived as the dropdown reopening then closing.
  const getPosition = (): React.CSSProperties => {
    const trigger = triggerRef?.current;
    if (!trigger) return { position: "absolute" };
    return computePosition(
      trigger.getBoundingClientRect(),
      resolvedSide,
      align,
      sideOffset,
      matchTriggerWidth,
    );
  };

  const isPendingFadeIn = open && !isAnimating;
  const isClosing = !open;

  const transformOrigin =
    resolvedSide === "bottom"
      ? "top"
      : resolvedSide === "top"
      ? "bottom"
      : resolvedSide === "left"
      ? "right"
      : "left";

  const animationStyle: React.CSSProperties = isPendingFadeIn
    ? { opacity: 0, animation: "none", transformOrigin }
    : isClosing
    ? {
        animation: "dropdownMenuFadeOut 100ms ease-in forwards",
        transformOrigin,
      }
    : {
        animation: "dropdownMenuFadeIn 150ms ease-out forwards",
        transformOrigin,
      };

  const content = (
    <div
      ref={contentRef}
      role="menu"
      aria-orientation="vertical"
      style={{ ...(lockedPositionRef.current ?? getPosition()), ...animationStyle }}
      className={`min-w-10xl overflow-hidden rounded-md bg-primary py-sm shadow-dropdown ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key !== "Escape") e.stopPropagation(); }}
    >
      {children}
    </div>
  );

  if (portal) {
    return createPortal(content, document.body);
  }

  return content;
}
